import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  coreExtensionData,
  createExtension,
  createExtensionInput,
} from '@backstage/frontend-plugin-api';
import { API_STATISTICS, REGISTERS } from '../portaal/api';

/**
 * nldd-* links render real anchors inside shadow roots. Turning same-origin
 * clicks into client-side navigation keeps the Backstage session and state.
 */
function useInternalLinks() {
  const navigate = useNavigate();
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    const onClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      const anchor = event
        .composedPath()
        .find(
          (node): node is HTMLAnchorElement =>
            node instanceof HTMLAnchorElement,
        );
      if (!anchor?.href || anchor.target === '_blank' || anchor.download) {
        return;
      }
      const url = new URL(anchor.href);
      const isApiDownload = url.pathname.startsWith('/api/');
      if (url.origin === window.location.origin && !isApiDownload) {
        event.preventDefault();
        navigate(`${url.pathname}${url.search}${url.hash}`);
      }
    };
    element.addEventListener('click', onClick);
    return () => element.removeEventListener('click', onClick);
  }, [navigate]);

  return ref;
}

/** What a register page itself does not offer from here: adding to it, and its own figures. */
function FooterLinks() {
  return (
    <nldd-container padding-block="32" layout="grid" sm-column-count="1" column-count="2" lg-column-count="3" gap="24">
      <nldd-container layout="stack" gap="8">
        <nldd-title size="5" heading-level="2" text="Registers" />
        <nldd-link size="sm" href="/registers" text="Alle registers" />
        <nldd-link size="sm" href={API_STATISTICS.url} text={API_STATISTICS.title} />
      </nldd-container>
      <nldd-container layout="stack" gap="8">
        <nldd-title size="5" heading-level="2" text="Iets aanmelden" />
        {REGISTERS.flatMap(register =>
          'addUrl' in register
            ? [<nldd-link key={register.key} size="sm" href={register.addUrl} text={register.addLabel} />]
            : [],
        )}
      </nldd-container>
    </nldd-container>
  );
}

function PortalLayout(props: { nav: ReactNode; content: ReactNode }) {
  const ref = useInternalLinks();
  return (
    <nldd-shell ref={ref}>
      <nldd-page>
        {props.nav}
        <main id="inhoud" className="portal-main" tabIndex={-1}>
          {props.content}
        </main>
        <nldd-page-footer slot="footer" width="full">
          <FooterLinks />
          <nldd-page-footer-legal-bar slot="legal-bar">
            <nldd-page-footer-legal-bar-item text="Nederlandse Digitale Dienst" />
            <nldd-page-footer-legal-bar-item
              text="Regels voor overheidssoftware"
              href="https://nerds.digitaledienst.overheid.nl"
            />
            <nldd-page-footer-legal-bar-item
              text="Toegankelijkheid van dit portaal"
              href="/toegankelijkheid"
            />
            <nldd-page-footer-legal-bar-item text="Demo: geen productiegegevens" />
          </nldd-page-footer-legal-bar>
        </nldd-page-footer>
      </nldd-page>
    </nldd-shell>
  );
}

/** Replaces the sidebar layout with the page structure of the design system. */
export const appLayout = createExtension({
  name: 'layout',
  attachTo: { id: 'app/root', input: 'children' },
  inputs: {
    nav: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
    content: createExtensionInput([coreExtensionData.reactElement], {
      singleton: true,
    }),
  },
  output: [coreExtensionData.reactElement],
  factory: ({ inputs }) => [
    coreExtensionData.reactElement(
      <PortalLayout
        nav={inputs.nav.get(coreExtensionData.reactElement)}
        content={inputs.content.get(coreExtensionData.reactElement)}
      />,
    ),
  ],
});
